import { PlusOutlined } from '@ant-design/icons';
import { Badge, Button, Empty, Modal, Pagination, Table } from 'antd';
import dayjs from 'dayjs';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import tw from 'twin.macro';

import { PATH } from '@/Route';
import Header from '@/components/list/Header';
import { useAdminNoticesMain } from '@/hooks/queries/admin';
import useBelowWidth from '@/hooks/useBelowWidth';
import usePagination from '@/hooks/usePagination';
import type { AdminNoticeListItem, NoticeBranch } from '@/types/admin';

const NoticeList = () => {
  const navigate = useNavigate();
  const pagination = usePagination();
  const query = useAdminNoticesMain({ ...pagination.query });
  const max1600 = useBelowWidth(1600);

  const [branchModalBranches, setBranchModalBranches] = useState<
    NoticeBranch[] | null
  >(null);

  const dataSource = useMemo(() => {
    const pinned = query.data?.pinnedNotices ?? [];
    const regular = query.data?.notices.results ?? [];
    return [...pinned, ...regular];
  }, [query.data]);

  return (
    <div css={tw`flex flex-col gap-[16px]`}>
      <Table<AdminNoticeListItem>
        title={() => (
          <Header
            showTotalCount
            showSizeSelector
            pagination={{
              total: query.data?.notices.count,
              ...pagination,
            }}
            extra={
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => {
                  navigate(PATH.NOTICE_MANAGE.REGISTER);
                }}
              >
                공지 작성
              </Button>
            }
          />
        )}
        locale={{
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="등록된 공지사항이 없어요"
            />
          ),
        }}
        scroll={{ x: '100%' }}
        tableLayout={max1600 ? 'auto' : 'fixed'}
        showSorterTooltip={false}
        columns={[
          {
            dataIndex: 'title',
            title: '제목',
            width: 'auto',
            render: (value, record) => {
              const hasBranches =
                !record.isGlobal && record.branches.length > 0;
              const isMultiBranch = record.branches.length >= 2;
              return (
                <p
                  css={[
                    tw`block w-full truncate`,
                    max1600 && tw`max-w-[400px]`,
                  ]}
                >
                  {record.isPinned && (
                    <Badge
                      count="공지"
                      color="#1677ff"
                      css={tw`mr-[8px] align-middle`}
                    />
                  )}
                  {hasBranches &&
                    (isMultiBranch ? (
                      <>
                        <button
                          type="button"
                          css={tw`p-0 bg-transparent border-0 text-[#8c8c8c] cursor-pointer hover:underline`}
                          onClick={(e) => {
                            e.stopPropagation();
                            setBranchModalBranches(record.branches);
                          }}
                        >
                          [{record.branches[0].branchName} 외{' '}
                          {record.branches.length - 1}개 지점]
                        </button>{' '}
                      </>
                    ) : (
                      <span css={tw`text-[#8c8c8c]`}>
                        [{record.branches[0].branchName}]{' '}
                      </span>
                    ))}
                  <span css={record.isPinned ? tw`font-bold` : tw`font-normal`}>
                    {value}
                  </span>
                </p>
              );
            },
          },
          {
            key: 'target',
            align: 'center',
            title: '대상',
            width: '5%',
            render: (_, record) => (record.isGlobal ? '전체' : '지점'),
          },
          {
            dataIndex: 'creatorName',
            title: '작성자',
            width: '10%',
          },
          {
            dataIndex: 'createdAt',
            title: '작성일',
            width: '7%',
            render: (value) => dayjs(value).format('YYYY-MM-DD'),
          },
          {
            dataIndex: 'publishedAt',
            title: '게시일',
            width: '7%',
            render: (value) =>
              value ? dayjs(value).format('YYYY-MM-DD') : '-',
          },
          {
            key: 'status',
            title: '상태',
            width: '10%',
            align: 'center',
            render: (_, record) => {
              if (record.isDeleted)
                return <Badge status="error" text="삭제됨" />;
              if (!record.publishedAt)
                return <Badge status="default" text="게시 안함" />;
              return <Badge status="success" text="게시중" />;
            },
          },
          {
            dataIndex: 'viewCount',
            title: '조회수',
            width: '5%',
            align: 'center',
            render: (value: number) => value.toLocaleString(),
          },
        ]}
        dataSource={dataSource}
        rowKey={(row) => row.id}
        pagination={false}
        onRow={(record) => ({
          onClick: () => {
            navigate(`${PATH.NOTICE_MANAGE.INDEX}/${record.id}`);
          },
          role: 'button',
        })}
      />
      {query.data?.notices.count !== undefined && (
        <div css={tw`flex justify-center`}>
          <Pagination
            current={pagination.query.page}
            pageSize={pagination.query.size}
            total={query.data.notices.count}
            onChange={(page, size) =>
              pagination.setPaginationValues({ page, size })
            }
            showQuickJumper={false}
            showSizeChanger={false}
          />
        </div>
      )}
      <Modal
        title="지점 목록"
        open={branchModalBranches !== null}
        onCancel={() => setBranchModalBranches(null)}
        footer={null}
        centered
        width={800}
      >
        <ul
          css={tw`grid grid-cols-5 gap-x-[16px] gap-y-[8px] p-0 m-0 list-none`}
        >
          {branchModalBranches?.map((branch) => (
            <li key={branch.branchId} css={tw`truncate`}>
              {branch.branchName}
            </li>
          ))}
        </ul>
      </Modal>
    </div>
  );
};

export default NoticeList;
